/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Retry strategy", lastUpdatedDate = new Date(1713484800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Retry strategy",
        description: null,
        lastUpdatedDate: "Fri Apr 19 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Example retry scenario",
          "depth": 0,
          "id": "example-retry-scenario"
        }, {
          "value": "Split retries",
          "depth": 0,
          "id": "split-retries"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }, {
          "value": "If part of my sync failed, does Hightouch retry every record in the sync?",
          "depth": 1,
          "id": "if-part-of-my-sync-failed-does-hightouch-retry-every-record-in-the-sync"
        }, {
          "value": "How can I be alerted to failing records or syncs?",
          "depth": 1,
          "id": "how-can-i-be-alerted-to-failing-records-or-syncs"
        }, {
          "value": "How can I avoid Hightouch retrying invalid rows?",
          "depth": 1,
          "id": "how-can-i-avoid-hightouch-retrying-invalid-rows"
        }, {
          "value": "Why is Hightouch retrying a removed row?",
          "depth": 1,
          "id": "why-is-hightouch-retrying-a-removed-row"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      ol: "ol",
      li: "li",
      strong: "strong",
      h2: "h2",
      ul: "ul",
      em: "em",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.p, {
        children: ["Whenever a sync runs, Hightouch tracks all records that the destination rejects using their ", _jsx(_components.a, {
          href: "/getting-started/concepts#unique-primary-key-requirement",
          children: "primary key"
        }), ". Hightouch then automatically retries syncing these records two ways:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Within an Active Sync:"
          }), " If Hightouch receives any record-level errors during an active sync, it automatically retries those records at an exponential backoff until it meets a destination-dependent failure threshold. Hightouch's retry process is similar to the one ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/general/latest/gr/api-retries.html",
            children: "described by AWS"
          }), ". Some destinations support ", _jsx(_components.a, {
            href: "#split-retries",
            children: "split retries"
          }), " as part of the active sync retry strategy."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "On Subsequent Syncs:"
          }), " Hightouch keeps an index of all failed records and automatically retries them on every subsequent sync until they succeed."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "example-retry-scenario",
        children: "Example retry scenario"
      }), "\n", _jsx(_components.p, {
        children: "Imagine you are trying to sync 10,000 records to Salesforce in a single sync, and five of the records are locked within Salesforce at the beginning of the sync."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "During that sync run, imagine two of those five records become available for update. Because Hightouch performs retries during an active sync, these two will update successfully during the sync run."
        }), "\n", _jsx(_components.li, {
          children: "On the subsequent sync run, Hightouch automatically retries the remaining three records. Assuming they're unlocked by that point, they will successfully update on the subsequent sync. Hightouch continues to retry the failed records until they are successfully synced."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "split-retries",
        children: "Split retries"
      }), "\n", _jsxs(_components.p, {
        children: ["Some destinations count all records in a batch request as rejected if the request contains a single invalid record.\nFor some of these, Hightouch provides you the option of enabling ", _jsx(_components.strong, {
          children: "split retries"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you enable split retries, Hightouch splits the rejected batch in half and retries each sub-batch separately.\nIf either of those sub-batches fail, Hightouch splits ", _jsx(_components.em, {
          children: "it"
        }), " in half and retries each sub-batch separately.\nHightouch repeats this process until the destination has marked each record in the batch as successful or rejected."]
      }), "\n", _jsxs(_components.p, {
        children: ["The split retry strategy allows you to pinpoint which records are getting rejected with which errors.\nIt also reduces the number of valid records that get retried.\nSplit retries are part of the ", _jsx(_components.a, {
          href: "/syncs/retries",
          children: "active sync retry strategy"
        }), ".\nHightouch retries records that are still rejected at the end of the split retry process in the subsequent sync."]
      }), "\n", _jsx(_components.p, {
        children: "To see if a destination supports split retries, refer to its docs page."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Enabling split retries may lead to longer sync times."
        })
      }), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "if-part-of-my-sync-failed-does-hightouch-retry-every-record-in-the-sync",
        children: "If part of my sync failed, does Hightouch retry every record in the sync?"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch generally only retries failed records.\nFor example, if you try to sync 1,000 records to Salesforce, but only 600 pass, Hightouch retries only the 400 that failed."
      }), "\n", _jsxs(_components.p, {
        children: ["The exception to this is when a destination marks an entire batch as failed, even if only some of the records are invalid.\nIn that case, Hightouch retries the entire failed batch.\nEnabling ", _jsx(_components.a, {
          href: "#split-retries",
          children: "split retries"
        }), " can help to reduce the number of valid records that get retried."]
      }), "\n", _jsx(_components.h3, {
        id: "how-can-i-be-alerted-to-failing-records-or-syncs",
        children: "How can I be alerted to failing records or syncs?"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch retries failed records until the underlying issue is addressed.\nTo become aware of failed records or syncs and fix them, it's best to set up ", _jsx(_components.a, {
          href: "/syncs/alerting",
          children: "alerting"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "how-can-i-avoid-hightouch-retrying-invalid-rows",
        children: "How can I avoid Hightouch retrying invalid rows?"
      }), "\n", _jsx(_components.p, {
        children: "If one or more of your records have malformed data that won't be accepted by the destination, Hightouch retries them and they will continue to be rejected."
      }), "\n", _jsxs(_components.p, {
        children: ["If you don't want to sync these records to the destination, you can ", _jsx(_components.a, {
          href: "/models/sql-editor",
          children: "edit your model's definition"
        }), " to filter out these rows.\nThe SQL query you use to filter out invalid rows will depend on your destination's definition of accepted values."]
      }), "\n", _jsx(_components.h3, {
        id: "why-is-hightouch-retrying-a-removed-row",
        children: "Why is Hightouch retrying a removed row?"
      }), "\n", _jsxs(_components.p, {
        children: ["If rows are removed from your model query result and you've configured ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " for your sync, Hightouch sends requests to your destination to clear or delete removed records. Hightouch either clears or deletes the removed records from the destination based on the delete behavior setting you configured."]
      }), "\n", _jsx(_components.p, {
        children: "If a sync is unable to clear or delete certain destination records, Hightouch retries the request during the following sync run. This can happen because the destination rejects the request or if the destination's API is inaccessible."
      }), "\n", _jsx(_components.p, {
        children: "If you don't want the sync to retry removed rows in future sync runs, you can choose one of the following options:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Set delete behavior to ", _jsx(_components.strong, {
            children: "Do nothing"
          }), ". Keep in mind that this affects all rows removed from the model's query results, not just the ones being retried."]
        }), "\n", _jsxs(_components.li, {
          children: ["Temporarily set delete behavior to ", _jsx(_components.strong, {
            children: "Do nothing"
          }), ", run the sync ", _jsx(_components.a, {
            href: "/syncs/schedule-sync-ui#manual-runs",
            children: "manually"
          }), " once,\nand then reconfigure the sync to the delete behavior setting you would like. This clears out the removed rows from the ", _jsx(_components.a, {
            href: "/getting-started/concepts#change-data-capture",
            children: "diff file created by CDC"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/syncs/overview#reset-cdc",
            children: "Reset change data capture (CDC)"
          }), " without a backfill."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Please keep in mind that:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["with option 2), during the run where delete behavior is set to ", _jsx(_components.strong, {
            children: "Do nothing"
          }), ", no model rows that CDC marks as \"removed\" are synced to the destination"]
        }), "\n", _jsx(_components.li, {
          children: "with option 3), any changes to your model (added, changed, or removed rows) between the previous run and the Reset CDC run aren't synced to the destination"
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "To minimize the risk of model data not being synced to the destination, we recommend triggering a regular sync run right before following through with options 2) or 3)."
        })
      })]
    });
  }
}
export default MDXContent;
